import React, { useEffect, useState } from 'react';
import AuthService from '../../services/authService';
import Spinner from '../Spinner';
import { InvoiceSearchUser } from '../../types/adminInvoice';

interface InvoiceUserSearchProps {
  onSelect: (user: InvoiceSearchUser) => void;
  /** Fires whenever the query text changes (including clear). */
  onQueryChange?: (query: string) => void;
}

const authService = new AuthService();

const InvoiceUserSearch: React.FC<InvoiceUserSearchProps> = ({ onSelect, onQueryChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<InvoiceSearchUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const updateQuery = (value: string) => {
    setSearchQuery(value);
    onQueryChange?.(value);
  };

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const debounceTimer = setTimeout(async () => {
      try {
        const response = await authService.searchUsers(searchQuery, 10);
        if (response.status === 200) {
          setSearchResults(response.data.users || []);
        } else {
          setSearchResults([]);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSelect = (user: InvoiceSearchUser) => {
    updateQuery('');
    setSearchResults([]);
    onSelect(user);
  };

  const handleClear = () => {
    updateQuery('');
    setSearchResults([]);
  };

  return (
    <div>
      <label htmlFor="invoiceUserSearch" className="block text-sm font-medium text-gray-700 mb-1">
        Search user to invoice
      </label>
      <div className="relative max-w-xl">
        <div className="flex">
          <input
            type="text"
            id="invoiceUserSearch"
            className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search another user by name, username, or email"
            value={searchQuery}
            onChange={(e) => updateQuery(e.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={handleClear}
            className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
            aria-label={searchQuery ? 'Clear search' : 'Search'}
          >
            {searchQuery ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </button>
        </div>

        {searchQuery.length > 0 && (
          <div className="mt-2 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-10 absolute shadow-lg">
            {searchLoading ? (
              <div className="p-4 flex justify-center items-center">
                <Spinner size="sm" />
                <span className="ml-2 text-gray-600">Searching...</span>
              </div>
            ) : searchResults.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {searchResults.map((user) => (
                  <li
                    key={user.id}
                    className="px-4 py-3 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleSelect(user)}
                  >
                    <div className="font-medium text-gray-900">{user.full_name}</div>
                    <div className="text-sm text-gray-600">{user.username}</div>
                    <div className="text-sm text-gray-500 flex justify-between mt-0.5">
                      <span>{user.email}</span>
                      {user.team_name && <span className="text-gray-400">{user.team_name}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : searchQuery.length >= 2 ? (
              <div className="p-4 text-center text-gray-500">No users found</div>
            ) : null}
          </div>
        )}
      </div>

      {searchQuery.length > 0 && searchQuery.length < 2 && (
        <p className="mt-1 text-sm text-gray-500">Type at least 2 characters to search</p>
      )}
    </div>
  );
};

export default InvoiceUserSearch;
